import { api } from "./api";

export type MailboxReplyTemplate = {
  id: string;
  name: string;
  intent: "lead" | "support";
  subject: string;
  body: string;
};

export type MailboxSettings = {
  id: string | null;
  label: string;
  host: string;
  port: number;
  username: string;
  use_ssl: boolean;
  folder: string;
  enabled: boolean;
  scan_interval_minutes: number;
  auto_convert: boolean;
  auto_create_leads: boolean;
  auto_create_support: boolean;
  auto_assign: boolean;
  auto_reply: boolean;
  reply_templates: MailboxReplyTemplate[];
  auth_mode: "basic" | "microsoft";
  oauth_connected: boolean;
  oauth_account_email: string | null;
  microsoft_oauth_available: boolean;
  password_configured: boolean;
  last_tested_at: string | null;
  last_error: string | null;
  last_scan_at: string | null;
  scan_error: string | null;
  ai_ready: boolean;
  queue_access?: boolean;
};

export type MailMessage = {
  id: string;
  from_email: string;
  from_name: string;
  to_emails: string;
  cc_emails: string;
  subject: string;
  body_text: string;
  received_at: string;
  intent: string;
  confidence: number | null;
  summary: string;
  suggested_reply: string;
  priority: string;
  status: string;
  claimed_by: string | null;
  claimed_name?: string | null;
  waiting_minutes: number;
  thread_key: string;
  thread_size: number;
  lead_id: string | null;
  ticket_id: string | null;
  activity_id: string | null;
  company_id: string | null;
  contact_id: string | null;
  internal_note: string;
  ai_error: string | null;
  thread?: Array<{
    id: string;
    subject: string;
    from_name: string;
    from_email: string;
    received_at: string;
    summary: string;
  }>;
};

export type MeetingItem = {
  id: string;
  source: "activity" | "event" | "follow_up";
  title: string;
  starts_at: string;
  ends_at: string | null;
  company_id?: string | null;
  owner_id: string | null;
  owner_name: string;
  company_name: string;
  stage: string;
  stage_kind?: "lead" | "opportunity" | "";
  stage_label: string;
  opportunity_title: string;
  playbook: string;
  close_guidance: string;
  ai_ready?: boolean;
};

export type Schedule = {
  scope: "own" | "team";
  ai_ready: boolean;
  meetings: MeetingItem[];
  events: MeetingItem[];
  follow_ups: MeetingItem[];
};

function base(orgId: string): string {
  return `/org/${orgId}/crm`;
}

export const mailboxApi = {
  settings: (orgId: string) => api.get<{ mailbox: MailboxSettings }>(`${base(orgId)}/mailbox`),
  save: (orgId: string, body: Partial<MailboxSettings> & { password?: string }) =>
    api.put<{ mailbox: MailboxSettings }>(`${base(orgId)}/mailbox`, body),
  test: (orgId: string) => api.post<{ ok: boolean }>(`${base(orgId)}/mailbox/test`, {}),
  microsoftStart: (orgId: string, body: { enable_when_connected: boolean }) =>
    api.post<{ authorization_url: string }>(`${base(orgId)}/mailbox/microsoft/start`, body),
  microsoftDisconnect: (orgId: string) =>
    api.post<{ mailbox: MailboxSettings }>(`${base(orgId)}/mailbox/microsoft/disconnect`, {}),
  scan: (orgId: string) => api.post<{ stored: number; classified: number; skipped?: boolean }>(`${base(orgId)}/mailbox/scan`, {}),
  log: (orgId: string, body: { from_email?: string; from_name?: string; subject: string; body_text?: string }) =>
    api.post<{ message: MailMessage }>(`${base(orgId)}/mailbox/messages`, body),
  messages: (orgId: string, view = "open", intent?: string) => {
    const params = new URLSearchParams({ view });
    if (intent) params.set("intent", intent);
    return api.get<{ messages: MailMessage[] }>(`${base(orgId)}/mailbox/messages?${params}`);
  },
  message: (orgId: string, id: string) => api.get<{ message: MailMessage }>(`${base(orgId)}/mailbox/messages/${id}`),
  convert: (orgId: string, id: string, body: Record<string, unknown>) =>
    api.post<{ message: MailMessage }>(`${base(orgId)}/mailbox/messages/${id}/convert`, body),
  claim: (orgId: string, id: string) => api.post<{ message: MailMessage }>(`${base(orgId)}/mailbox/messages/${id}/claim`, {}),
  snooze: (orgId: string, id: string, until: string) =>
    api.post<{ message: MailMessage }>(`${base(orgId)}/mailbox/messages/${id}/snooze`, { until }),
  ignore: (orgId: string, id: string) => api.post<{ message: MailMessage }>(`${base(orgId)}/mailbox/messages/${id}/ignore`, {}),
  note: (orgId: string, id: string, note: string) =>
    api.post<{ message: MailMessage }>(`${base(orgId)}/mailbox/messages/${id}/note`, { note }),
  schedule: (orgId: string, starts: string, ends: string) =>
    api.get<Schedule>(`${base(orgId)}/calendar/meetings?starts=${encodeURIComponent(starts)}&ends=${encodeURIComponent(ends)}`),
  guidance: (orgId: string, source: string, id: string) =>
    api.post<{ meeting: MeetingItem }>(`${base(orgId)}/calendar/meetings/guidance`, { source, id }),
};
